import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Sparkles, Check, X, Trash2, Wand2, Users } from "lucide-react";
import { ScoreExplanation } from "@/components/trends/ScoreExplanation";
import { SourceTransparency } from "@/components/trends/SourceTransparency";
import { CompetitorOverlay, type CompetitorsAnalysis } from "@/components/trends/CompetitorOverlay";
import { DetailedBlueprint, type DetailedBlueprintData } from "@/components/trends/DetailedBlueprint";
import { ValidationTools } from "@/components/trends/ValidationTools";
import { CronAlertsPanel } from "@/components/admin/CronAlertsPanel";
import { CronAlertRecipients } from "@/components/admin/CronAlertRecipients";
import { CronAlertSettings } from "@/components/admin/CronAlertSettings";

type Trend = {
  id: string;
  title: string;
  summary: string;
  niche: string | null;
  tags: string[];
  velocity_score: number;
  volume_score: number;
  relevance_score: number;
  total_score: number;
  status: "pending" | "approved" | "rejected";
  blueprint: Record<string, unknown>;
  source_slugs: string[];
  signal_count: number;
  curator_notes: string | null;
  created_at: string;
};

type ScanRun = {
  id: string;
  source_slug: string | null;
  kind: string;
  status: string;
  signals_collected: number;
  trends_created: number;
  error: string | null;
  started_at: string;
  finished_at: string | null;
};

export function TrendsTab() {
  const [trends, setTrends] = useState<Trend[]>([]);
  const [runs, setRuns] = useState<ScanRun[]>([]);
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scoring, setScoring] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: t }, { data: r }] = await Promise.all([
      supabase.from("trends").select("*").order("total_score", { ascending: false }).limit(100),
      supabase.from("scan_runs").select("*").order("started_at", { ascending: false }).limit(10),
    ]);
    setTrends((t as Trend[]) ?? []);
    setRuns((r as ScanRun[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    const { error } = await supabase.functions.invoke("scan-sources", { body: {} });
    setScanning(false);
    if (error) return toast.error(error.message);
    toast.success("Source scan complete");
    load();
  };

  const runScore = async () => {
    setScoring(true);
    const { data, error } = await supabase.functions.invoke("score-trends", { body: {} });
    setScoring(false);
    if (error) return toast.error(error.message);
    toast.success(`AI scoring done · ${data?.trends_created ?? 0} new trends`);
    load();
  };

  const setStatus = async (id: string, status: "approved" | "rejected") => {
    const { data: { user } } = await supabase.auth.getUser();
    const patch = {
      status,
      curated_at: new Date().toISOString(),
      curator_id: user?.id ?? null,
      published_at: status === "approved" ? new Date().toISOString() : null,
    };
    const trend = trends.find((t) => t.id === id);
    const { error } = await supabase.from("trends").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    setTrends((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    await supabase.functions.invoke("log-admin-action", {
      body: {
        action: status === "approved" ? "trend.approve" : "trend.reject",
        target_table: "trends",
        target_id: id,
        details: { title: trend?.title, previous_status: trend?.status, new_status: status },
      },
    });
    toast.success(status === "approved" ? "Trend approved & published" : "Trend rejected");
  };

  const remove = async (id: string) => {
    const trend = trends.find((t) => t.id === id);
    const { error } = await supabase.from("trends").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setTrends((prev) => prev.filter((t) => t.id !== id));
    await supabase.functions.invoke("log-admin-action", {
      body: {
        action: "trend.delete",
        target_table: "trends",
        target_id: id,
        details: { title: trend?.title, status: trend?.status },
      },
    });
    toast.success("Trend deleted");
  };

  const visible = trends.filter((t) => filter === "all" || t.status === filter);

  return (
    <div className="space-y-6">
      <CronAlertsPanel />
      <CronAlertSettings />
      <CronAlertRecipients />
      <div className="rounded-xl border border-border bg-card p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold">Trends pipeline</h2>
          <p className="text-sm text-muted-foreground">
            Pull fresh signals from 7 sources, then cluster &amp; score with AI.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runScan} disabled={scanning}>
            {scanning ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Scan sources
          </Button>
          <Button onClick={runScore} disabled={scoring}>
            {scoring ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            Score with AI
          </Button>
        </div>
      </div>

      {runs.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">recent runs</div>
          <div className="flex flex-wrap gap-2">
            {runs.map((r) => (
              <span key={r.id} className={`font-mono text-[10px] rounded px-2 py-1 border ${
                r.status === "ok" ? "border-primary/40 text-primary"
                : r.status === "error" ? "border-destructive/40 text-destructive"
                : "border-border text-muted-foreground"}`}>
                {r.kind}{r.source_slug ? `:${r.source_slug}` : ""} · {r.status}
                {r.signals_collected ? ` · ${r.signals_collected}s` : ""}
                {r.trends_created ? ` · ${r.trends_created}t` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {(["pending", "approved", "rejected", "all"] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`font-mono text-xs uppercase px-3 py-1.5 rounded border transition ${
              filter === f ? "border-primary text-primary bg-primary/5"
              : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            {f} {f !== "all" && `(${trends.filter((t) => t.status === f).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          No trends in this view. Run a scan, then score.
        </div>
      ) : (
        <div className="grid gap-4">
          {visible.map((t) => (
            <TrendCard key={t.id} trend={t} onApprove={() => setStatus(t.id, "approved")}
              onReject={() => setStatus(t.id, "rejected")} onDelete={() => remove(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrendCard({ trend, onApprove, onReject, onDelete }: {
  trend: Trend; onApprove: () => void; onReject: () => void; onDelete: () => void;
}) {
  const [notes, setNotes] = useState(trend.curator_notes ?? "");
  const [busy, setBusy] = useState<null | "approve" | "reject" | "delete" | "notes" | "blueprint" | "competitors">(null);
  const [blueprint, setBlueprint] = useState<Record<string, unknown>>(trend.blueprint ?? {});
  const bp = blueprint as any;
  const competitors = bp?.competitors_analysis as CompetitorsAnalysis | undefined;
  const generateBlueprint = async () => {
    setBusy("blueprint");
    const { data, error } = await supabase.functions.invoke("generate-blueprint", {
      body: { trend_id: trend.id },
    });
    setBusy(null);
    if (error) {
      const msg = (data as { error?: string } | null)?.error ?? error.message;
      return toast.error(msg);
    }
    const next = (data as { blueprint?: Record<string, unknown> })?.blueprint;
    if (!next) return toast.error("Could not generate blueprint");
    setBlueprint(next);
    toast.success("Blueprint generated");
  };
  const generateCompetitors = async () => {
    setBusy("competitors");
    const { data, error } = await supabase.functions.invoke("generate-competitors", {
      body: { trend_id: trend.id },
    });
    setBusy(null);
    if (error) {
      const msg = (data as { error?: string } | null)?.error ?? error.message;
      return toast.error(msg);
    }
    const ca = (data as { competitors_analysis?: CompetitorsAnalysis })?.competitors_analysis;
    if (!ca) return toast.error("Could not generate competitor analysis");
    setBlueprint((prev) => ({ ...prev, competitors_analysis: ca }));
    toast.success("Competitor analysis generated");
  };
  const saveNotes = async () => {
    if ((notes ?? "") === (trend.curator_notes ?? "")) return;
    setBusy("notes");
    const { error } = await supabase
      .from("trends")
      .update({ curator_notes: notes })
      .eq("id", trend.id);
    setBusy(null);
    if (error) return toast.error(error.message);
    await supabase.functions.invoke("log-admin-action", {
      body: {
        action: "trend.notes_update",
        target_table: "trends",
        target_id: trend.id,
        details: {
          title: trend.title,
          previous_notes: trend.curator_notes ?? "",
          new_notes: notes,
        },
      },
    });
    toast.success("Notes saved");
  };
  const wrap = (k: "approve" | "reject" | "delete", fn: () => Promise<void> | void) => async () => {
    setBusy(k);
    try { await fn(); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-primary/40 text-primary">
              {trend.status}
            </span>
            {trend.niche && (
              <span className="font-mono text-[10px] text-muted-foreground">{trend.niche}</span>
            )}
            <span className="font-mono text-[10px] text-muted-foreground">
              · {trend.signal_count} signals · {trend.source_slugs.join(", ")}
            </span>
          </div>
          <h3 className="font-display text-lg font-bold">{trend.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{trend.summary}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-3xl font-bold text-primary">{trend.total_score}</div>
          <div className="font-mono text-[10px] text-muted-foreground">total score</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <ScoreBar label="Velocity" value={trend.velocity_score} />
        <ScoreBar label="Volume" value={trend.volume_score} />
        <ScoreBar label="Relevance" value={trend.relevance_score} />
      </div>

      <ScoreExplanation
        velocity={trend.velocity_score}
        volume={trend.volume_score}
        relevance={trend.relevance_score}
        sourceSlugs={trend.source_slugs}
        breakdown={bp?.score_breakdown}
      />

      <SourceTransparency sourceSlugs={trend.source_slugs} signalCount={trend.signal_count} />

      {trend.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {trend.tags.map((t) => (
            <span key={t} className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground">
              #{t}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 rounded-lg border border-border/60 bg-secondary/30 p-3 text-sm">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="font-mono text-[10px] uppercase text-muted-foreground">blueprint</div>
          <Button size="sm" variant="outline" onClick={generateBlueprint} disabled={!!busy}>
            {busy === "blueprint" ? <Loader2 className="size-4 animate-spin" /> : <Wand2 className="size-4" />}
            {bp?.target_buyer ? "Regenerate" : "Generate blueprint"}
          </Button>
        </div>
        {bp?.target_buyer ? (
          <div className="space-y-2">
            <div><b>Buyer:</b> {bp.target_buyer}</div>
            {bp.target_audience && (
              <div><b>Audience:</b> <span className="text-muted-foreground">{bp.target_audience}</span></div>
            )}
            {bp.product_concept && (
              <div><b>Concept:</b> <span className="text-muted-foreground">{bp.product_concept}</span></div>
            )}
            <div><b>Format:</b> {bp.format} · <b>Price:</b> {bp.price_range}</div>
            {Array.isArray(bp.keyword_ideas) && bp.keyword_ideas.length > 0 && (
              <div>
                <b>Keywords:</b>{" "}
                <span className="flex flex-wrap gap-1 mt-1">
                  {bp.keyword_ideas.map((k: string) => (
                    <span key={k} className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                      {k}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {Array.isArray(bp.launch_steps) && (
              <div>
                <b>Launch steps:</b>
                <ol className="list-decimal list-inside mt-1 text-muted-foreground">
                  {bp.launch_steps.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            No blueprint yet. Generate one to get a starter audience, keywords, and product concept.
          </p>
        )}
      </div>

      <div className="mt-4 rounded-lg border border-border/60 bg-secondary/30 p-3 text-sm">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="font-mono text-[10px] uppercase text-muted-foreground">competitor overlay</div>
          <Button size="sm" variant="outline" onClick={generateCompetitors} disabled={!!busy}>
            {busy === "competitors" ? <Loader2 className="size-4 animate-spin" /> : <Users className="size-4" />}
            {competitors ? "Regenerate" : "Generate competitors"}
          </Button>
        </div>
        {competitors ? (
          <CompetitorOverlay data={competitors} trendTitle={trend.title} />
        ) : (
          <p className="text-muted-foreground text-xs">
            No competitor analysis yet. Generate one to see who's playing in this niche and where the gaps are.
          </p>
        )}
      </div>

      <DetailedBlueprint
        trendId={trend.id}
        initial={bp?.detailed as DetailedBlueprintData | undefined}
        canRegenerate
      />
      <ValidationTools trendId={trend.id} trendTitle={trend.title} tags={trend.tags} />

      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes}
        placeholder="Curator notes…" className="mt-3 text-sm" disabled={busy === "notes"} />

      <div className="flex justify-end gap-2 mt-3">
        {trend.status !== "approved" && (
          <Button size="sm" onClick={wrap("approve", onApprove)} disabled={!!busy}>
            {busy === "approve" ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            Approve
          </Button>
        )}
        {trend.status !== "rejected" && (
          <Button size="sm" variant="outline" onClick={wrap("reject", onReject)} disabled={!!busy}>
            {busy === "reject" ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
            Reject
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" disabled={!!busy} aria-label="Delete trend">
              {busy === "delete" ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this trend?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes "{trend.title}" and its curator notes. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={wrap("delete", onDelete)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex justify-between font-mono text-[10px] text-muted-foreground">
        <span>{label}</span><span>{value}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-1">
        <div className="h-full bg-primary" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
